// repairData.ts - 정비 모듈 목업 데이터

export type FaultSeverity = "위험" | "보통" | "주의";
export type RepairStatus = "대기중" | "진행중" | "완료" | "취소";

export interface FaultCode {
    id: string;
    lightName: string;
    iconType: "engine" | "battery" | "coolant" | "tire" | "oil" | "transmission" | "brake" | "abs";
    code: string;
    severity: FaultSeverity;
    description: string;
    action: string;
    manufacturer?: string;
}

export interface RepairShop {
    id: string;
    name: string;
    distance: number;         // km
    estimatedTime: number;    // minutes
    estimatedCost: number;    // KRW
    address: string;
    capability: string;       // e.g. "1~15톤"
    rating: number;
    phone: string;
    lat: number;              // mock map position (%)
    lng: number;
}

export interface RepairRecord {
    id: string;
    vehicleNumber: string;
    vehicleModel: string;
    faultName: string;
    faultCode: string;
    severity: FaultSeverity;
    shopName: string;
    shopPhone: string;
    requestTime: string;
    estimatedTime: number;    // minutes
    estimatedCost: number;    // KRW
    status: RepairStatus;
    driverName: string;
    completedTime?: string;
    notes?: string;
}

export interface CallItem {
    id: string;
    vehicleNumber: string;
    faultName: string;
    location: string;
    distance: number;
    estimatedCost: number;
    postedAt: string;
    timerSeconds: number;
    accepted: boolean;
}

// ── Mock Fault Codes (계기판 점등 유형 데이터베이스) ──────────────────────
export const MOCK_FAULT_CODES: FaultCode[] = [
    {
        id: "FC001",
        lightName: "엔진 경고등",
        iconType: "engine",
        code: "P0300",
        severity: "위험",
        description: "엔진 실린더에서 실화(점화 불량)가 감지되었습니다. 연료 공급 시스템 또는 점화 장치에 이상이 있을 수 있습니다.",
        action: "즉시 안전한 곳에 차량을 정차하고 엔진을 끄세요. 지속 운행 시 엔진 손상이 발생할 수 있습니다. 가까운 정비소에서 점검을 받으세요.",
    },
    {
        id: "FC002",
        lightName: "배터리 경고등",
        iconType: "battery",
        code: "B0001",
        severity: "위험",
        description: "충전 시스템 이상이 감지되었습니다. 발전기(알터네이터) 또는 배터리 자체의 문제일 수 있습니다.",
        action: "전기 소모를 최소화하고 즉시 가까운 정비소로 이동하세요. 방치 시 배터리 방전으로 운행 불가 상태가 됩니다.",
    },
    {
        id: "FC003",
        lightName: "냉각수 온도 경고등",
        iconType: "coolant",
        code: "P0217",
        severity: "위험",
        description: "엔진 냉각수 온도가 과열 범위에 도달했습니다. 냉각수 부족 또는 냉각 시스템 누수가 원인일 수 있습니다.",
        action: "즉시 안전한 장소에 차를 세우고 엔진을 끄세요. 냉각수가 충분히 식은 후 냉각수 레벨을 확인하고 정비소로 이동하세요.",
    },
    {
        id: "FC004",
        lightName: "타이어 공기압 경고등",
        iconType: "tire",
        code: "C0040",
        severity: "주의",
        description: "하나 이상의 타이어 공기압이 권장 범위 이하로 감지되었습니다. 타이어 펑크 또는 서서히 공기가 빠지는 상태일 수 있습니다.",
        action: "가까운 주유소 또는 타이어 전문점에서 공기압을 확인하고 보충하세요. 타이어 손상 여부도 함께 점검하세요.",
    },
    {
        id: "FC005",
        lightName: "오일 압력 경고등",
        iconType: "oil",
        code: "P0520",
        severity: "위험",
        description: "엔진 오일 압력이 비정상적으로 낮습니다. 오일 부족, 오일 누유 또는 오일 펌프 이상일 수 있습니다.",
        action: "즉시 운행을 중단하세요. 오일 압력 저하 상태로 운행하면 엔진이 심각하게 손상됩니다. 긴급 정비가 필요합니다.",
    },
    {
        id: "FC006",
        lightName: "변속기 경고등",
        iconType: "transmission",
        code: "P0700",
        severity: "보통",
        description: "변속기 제어 시스템에 이상이 감지되었습니다. 기어 변속이 원활하지 않거나 변속기 오일 상태가 불량할 수 있습니다.",
        action: "가까운 정비소에서 변속기 오일 상태 및 변속기 전자 제어 장치를 점검받으세요. 즉각적인 위험은 없으나 조기 점검이 권장됩니다.",
    },
    {
        id: "FC007",
        lightName: "브레이크 경고등",
        iconType: "brake",
        code: "C0031",
        severity: "위험",
        description: "브레이크 시스템 이상이 감지되었습니다. 브레이크 패드 마모, 브레이크 오일 부족 또는 유압 시스템 이상일 수 있습니다.",
        action: "즉시 안전한 곳에 주차하고 엔진을 끄세요. 브레이크 이상 상태에서의 운행은 매우 위험합니다. 즉각적인 정비가 필요합니다.",
    },
    {
        id: "FC008",
        lightName: "ABS 경고등",
        iconType: "abs",
        code: "C0035",
        severity: "보통",
        description: "ABS(잠김 방지 제동 시스템) 이상이 감지되었습니다. ABS 센서 또는 ABS 모듈에 이상이 있을 수 있습니다.",
        action: "일반 브레이크는 작동하지만 ABS가 비활성화된 상태입니다. 미끄러운 노면 주행에 주의하고 가까운 정비소에서 점검받으세요.",
    },
];

// ── Mock Repair Shops (주변 정비소 목록) ─────────────────────────────────
export const MOCK_REPAIR_SHOPS: RepairShop[] = [
    {
        id: "S001",
        name: "부산 트럭 종합 정비",
        distance: 0.8,
        estimatedTime: 90,
        estimatedCost: 120000,
        address: "부산 동구 초량로 45",
        capability: "1~15톤",
        rating: 4.8,
        phone: "051-123-4567",
        lat: 42,
        lng: 38,
    },
    {
        id: "S002",
        name: "중부 화물 전문 정비",
        distance: 1.4,
        estimatedTime: 60,
        estimatedCost: 98000,
        address: "부산 연제구 연산로 112",
        capability: "1~5톤",
        rating: 4.6,
        phone: "051-234-5678",
        lat: 55,
        lng: 62,
    },
    {
        id: "S003",
        name: "동부 대형 차량 정비",
        distance: 2.1,
        estimatedTime: 120,
        estimatedCost: 145000,
        address: "부산 수영구 수영로 33",
        capability: "5~15톤",
        rating: 4.7,
        phone: "051-345-6789",
        lat: 30,
        lng: 72,
    },
    {
        id: "S004",
        name: "남구 화물 서비스센터",
        distance: 2.8,
        estimatedTime: 75,
        estimatedCost: 89000,
        address: "부산 남구 용호동 221",
        capability: "1~10톤",
        rating: 4.4,
        phone: "051-456-7890",
        lat: 68,
        lng: 48,
    },
    {
        id: "S005",
        name: "해운대 전문 정비소",
        distance: 4.2,
        estimatedTime: 45,
        estimatedCost: 175000,
        address: "부산 해운대구 중동 87",
        capability: "1~15톤",
        rating: 4.9,
        phone: "051-567-8901",
        lat: 22,
        lng: 20,
    },
];

// ── Mock Repair Records (정비현황 목록) ───────────────────────────────────
export const MOCK_REPAIR_RECORDS: RepairRecord[] = [
    {
        id: "R001",
        vehicleNumber: "12가3456",
        vehicleModel: "현대 포터II 일렉트릭",
        faultName: "엔진 경고등",
        faultCode: "P0300",
        severity: "위험",
        shopName: "부산 트럭 종합 정비",
        shopPhone: "051-123-4567",
        requestTime: "2026-04-04 09:23",
        estimatedTime: 90,
        estimatedCost: 120000,
        status: "진행중",
        driverName: "김철수",
        notes: "엔진 실화 감지. 현장 도착 후 점화플러그 교체 진행 중",
    },
    {
        id: "R002",
        vehicleNumber: "34나7890",
        vehicleModel: "기아 봉고III EV",
        faultName: "배터리 경고등",
        faultCode: "B0001",
        severity: "위험",
        shopName: "중부 화물 전문 정비",
        shopPhone: "051-234-5678",
        requestTime: "2026-04-04 08:11",
        estimatedTime: 60,
        estimatedCost: 85000,
        status: "완료",
        driverName: "이영희",
        completedTime: "2026-04-04 09:15",
        notes: "발전기 단자 부식으로 인한 접촉 불량. 단자 세척 및 교체 완료",
    },
    {
        id: "R003",
        vehicleNumber: "56다1234",
        vehicleModel: "현대 포터II 일렉트릭",
        faultName: "오일 압력 경고등",
        faultCode: "P0520",
        severity: "위험",
        shopName: "동부 대형 차량 정비",
        shopPhone: "051-345-6789",
        requestTime: "2026-04-04 10:45",
        estimatedTime: 120,
        estimatedCost: 230000,
        status: "대기중",
        driverName: "박민준",
    },
    {
        id: "R004",
        vehicleNumber: "78라5678",
        vehicleModel: "기아 봉고III EV",
        faultName: "타이어 공기압 경고등",
        faultCode: "C0040",
        severity: "주의",
        shopName: "남구 화물 서비스센터",
        shopPhone: "051-456-7890",
        requestTime: "2026-04-04 07:30",
        estimatedTime: 30,
        estimatedCost: 25000,
        status: "완료",
        driverName: "최지우",
        completedTime: "2026-04-04 08:10",
    },
    {
        id: "R005",
        vehicleNumber: "90마9012",
        vehicleModel: "현대 포터II 일렉트릭",
        faultName: "변속기 경고등",
        faultCode: "P0700",
        severity: "보통",
        shopName: "해운대 전문 정비소",
        shopPhone: "051-567-8901",
        requestTime: "2026-04-04 11:02",
        estimatedTime: 75,
        estimatedCost: 95000,
        status: "진행중",
        driverName: "강서준",
        notes: "변속기 오일 교체 및 전자 모듈 리셋 진행 중",
    },
    {
        id: "R006",
        vehicleNumber: "12바3456",
        vehicleModel: "기아 봉고III EV",
        faultName: "ABS 경고등",
        faultCode: "C0035",
        severity: "보통",
        shopName: "부산 트럭 종합 정비",
        shopPhone: "051-123-4567",
        requestTime: "2026-04-04 06:55",
        estimatedTime: 90,
        estimatedCost: 110000,
        status: "완료",
        driverName: "윤미래",
        completedTime: "2026-04-04 08:30",
    },
    {
        id: "R007",
        vehicleNumber: "34사7890",
        vehicleModel: "현대 포터II 일렉트릭",
        faultName: "브레이크 경고등",
        faultCode: "C0031",
        severity: "위험",
        shopName: "동부 대형 차량 정비",
        shopPhone: "051-345-6789",
        requestTime: "2026-04-04 12:15",
        estimatedTime: 150,
        estimatedCost: 280000,
        status: "대기중",
        driverName: "정하늘",
    },
];

// ── Mock Call Items (콜 시스템 목업) ────────────────────────────────────
export const MOCK_CALLS: CallItem[] = [
    {
        id: "CALL001",
        vehicleNumber: "56다1234",
        faultName: "오일 압력 경고등 (P0520)",
        location: "부산 사상구 학장동",
        distance: 2.3,
        estimatedCost: 230000,
        postedAt: "방금 전",
        timerSeconds: 45,
        accepted: false,
    },
    {
        id: "CALL002",
        vehicleNumber: "34사7890",
        faultName: "브레이크 경고등 (C0031)",
        location: "부산 북구 덕천동",
        distance: 4.1,
        estimatedCost: 280000,
        postedAt: "2분 전",
        timerSeconds: 28,
        accepted: false,
    },
    {
        id: "CALL003",
        vehicleNumber: "78아5678",
        faultName: "엔진 경고등 (P0300)",
        location: "부산 금정구 구서동",
        distance: 6.8,
        estimatedCost: 120000,
        postedAt: "5분 전",
        timerSeconds: 12,
        accepted: false,
    },
];

export function formatCost(cost: number): string {
    return cost.toLocaleString("ko-KR") + "원";
}

export function formatTime(minutes: number): string {
    if (minutes < 60) return `${minutes}분`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}시간 ${m}분` : `${h}시간`;
}
